import chakraBold from '@fontsource/chakra-petch/files/chakra-petch-latin-700-normal.woff2?url';
import russoOne from '@fontsource/russo-one/files/russo-one-latin-400-normal.woff2?url';

/**
 * Transforma uma `PlayerCard` num PNG para partilhar (Instagram, WhatsApp).
 *
 * Um SVG desenhado como imagem nao pode ir buscar nada fora dele, por isso as
 * imagens (foto, icone) e as duas fontes da carta entram la dentro como
 * `data:` antes de o desenhar num canvas.
 */

async function asDataUrl(url: string): Promise<string> {
  if (url.startsWith('data:')) return url;
  const blob = await (await fetch(url)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('leitura falhou'));
    reader.readAsDataURL(blob);
  });
}

async function fontFaces(): Promise<string> {
  const [russo, chakra] = await Promise.all([asDataUrl(russoOne), asDataUrl(chakraBold)]);
  return [
    `@font-face{font-family:'Russo One';src:url(${russo}) format('woff2');font-weight:400}`,
    `@font-face{font-family:'Chakra Petch';src:url(${chakra}) format('woff2');font-weight:700}`,
  ].join('');
}

/** A carta como PNG, em `scale` vezes o tamanho do viewBox (300x420). */
export async function playerCardPng(svg: SVGSVGElement, scale = 3): Promise<Blob> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const box = svg.viewBox.baseVal;
  const width = Math.round(box.width * scale);
  const height = Math.round(box.height * scale);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  await Promise.all(
    Array.from(clone.querySelectorAll('image')).map(async (image) => {
      const href = image.getAttribute('href');
      if (href) image.setAttribute('href', await asDataUrl(href));
    }),
  );
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = await fontFaces();
  clone.insertBefore(style, clone.firstChild);

  const source = new XMLSerializer().serializeToString(clone);
  const img = new Image();
  img.decoding = 'async';
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
  await img.decode();
  // As fontes dentro de um SVG-imagem carregam a parte: sem esta espera o
  // Chrome desenha a carta sem texto.
  await new Promise((resolve) => setTimeout(resolve, 300));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas indisponivel');
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG falhou'))), 'image/png'),
  );
}

/** Descarrega a carta como `<nome>.png`. */
export async function downloadPlayerCard(svg: SVGSVGElement, filename: string): Promise<void> {
  const blob = await playerCardPng(svg);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
